import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MindPage } from "@/mind/MindPage";
import { DebateLab } from "@/mind/lab/DebateLab";
import "@/styles/mind.css";
import "@/styles/lab.css";

// Dev-only: expose the model bench on the console. It builds its own clients,
// so it is unaffected by whatever the page is doing.
if (import.meta.env.DEV) {
  void import("@/mind/bench/modelBench").then((m) => {
    (window as unknown as Record<string, unknown>).runModelBench = async (opts?: object) => {
      const r = await m.runModelBench(opts);
      (window as unknown as Record<string, unknown>).__benchResult = r;
      return r;
    };
    console.info("[the-mind] dev bench ready: await runModelBench()");
  });

  // A debate driven from the console, building its own clients exactly like the
  // model bench does. The UI path depends on React rendering, which a browser
  // throttles hard when the tab is not compositing; this proves the engine
  // independently of whether anything is on screen.
  //
  //   await runDebateDemo({ a: "gardening", b: "hermes", topic: "..." })
  (window as unknown as Record<string, unknown>).runDebateDemo = async (opts: {
    a?: string; b?: string; topic?: string; model?: string; rounds?: number; delayMs?: number;
    deliberate?: boolean;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, { runDebate }] = await Promise.all([
      import("@/mind/llm/client"),
      import("@/mind/lattice/embedClient"),
      import("@/mind/lattice/packStore"),
      import("@/mind/lattice/packs"),
      import("@/mind/debate"),
    ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes");
    if (!A || !B) throw new Error("unknown pack key");

    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });

    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu", (m: string) => console.info("[debate]", m));
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b", { onMessage: (m: string) => console.info("[debate]", m) });

    const turns = await runDebate(
      llm,
      embedder,
      { key: A.key, persona: packA.manifest.persona, pack: packA },
      { key: B.key, persona: packB.manifest.persona, pack: packB },
      {
        topic: opts.topic ?? "whether the moon's phase should govern when you sow",
        rounds: opts.rounds ?? 2,
        delayMs: opts.delayMs ?? 0,
        deliberate: opts.deliberate ?? true,
        onThink: (s2, note) => console.info(`[debate] ${s2.persona} thinks: ${note.slice(0, 100)}`),
        onTurnEnd: (t) => console.info(`[debate] ${t.persona}: ${t.text.slice(0, 120)}`),
      },
    );
    llm.dispose();
    embedder.dispose();
    (window as unknown as Record<string, unknown>).__debateResult = turns;
    return turns;
  };
  console.info("[the-mind] dev debate ready: await runDebateDemo()");

  // The agenda round on its own, so the smoke test and fitness probe can be
  // judged without paying for a whole debate.
  //   await runAgendaDemo({ a: "gardening", b: "hermes-trismegistus", topic: "..." })
  (window as unknown as Record<string, unknown>).runAgendaDemo = async (opts: {
    a?: string; b?: string; topic?: string; model?: string;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, { proposeAgenda }] =
      await Promise.all([
        import("@/mind/llm/client"),
        import("@/mind/lattice/embedClient"),
        import("@/mind/lattice/packStore"),
        import("@/mind/lattice/packs"),
        import("@/mind/debate"),
      ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes-trismegistus");
    if (!A || !B) throw new Error("unknown pack key");
    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });
    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu");
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b");
    const out = await proposeAgenda(
      llm,
      embedder,
      { key: A.key, persona: packA.manifest.persona, pack: packA },
      { key: B.key, persona: packB.manifest.persona, pack: packB },
      opts.topic ?? "whether the phase of the moon should govern when you sow seed",
    );
    llm.dispose();
    embedder.dispose();
    (window as unknown as Record<string, unknown>).__agenda = out;
    return out;
  };

  // Free's staging A/B: does topic-level retrieval on the FIRST think help or
  // homogenise the two voices? Registered prediction, three topics, both arms
  // on identical inputs.
  //   await runStagingAB()
  (window as unknown as Record<string, unknown>).runStagingAB = async (opts: {
    a?: string; b?: string; topics?: string[]; model?: string;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, dbt, vd] =
      await Promise.all([
        import("@/mind/llm/client"),
        import("@/mind/lattice/embedClient"),
        import("@/mind/lattice/packStore"),
        import("@/mind/lattice/packs"),
        import("@/mind/debate"),
        import("@/mind/pure/voiceDivergence"),
      ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes-trismegistus");
    if (!A || !B) throw new Error("unknown pack key");
    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });
    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu");
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b");

    const sa = { key: A.key, persona: packA.manifest.persona, pack: packA };
    const sb = { key: B.key, persona: packB.manifest.persona, pack: packB };
    const topics = opts.topics ?? [
      "whether the phase of the moon should govern when you sow seed",
      "whether a plant responds to its surroundings or to its own inner pattern",
      "what actually decides whether a seed germinates",
    ];

    const rows = [];
    for (const topic of topics) {
      for (const retrieve of [true, false]) {
        const sA = await dbt.prepareSpeaker(llm, embedder, sa, sb, topic, true, undefined, retrieve);
        const sB = await dbt.prepareSpeaker(llm, embedder, sb, sa, topic, false, undefined, retrieve);
        rows.push({
          topic: topic.slice(0, 42),
          retrieve,
          divergence: +vd.lexicalDivergence(sA.stagingNote, sB.stagingNote).toFixed(3),
          charsA: sA.stagingNote.length,
          charsB: sB.stagingNote.length,
          noteA: sA.stagingNote,
          noteB: sB.stagingNote,
        });
        console.info(`[ab] retrieve=${retrieve} div=${rows[rows.length - 1].divergence} ${topic.slice(0, 30)}`);
      }
    }
    llm.dispose();
    embedder.dispose();
    const mean = (xs: number[]) => xs.reduce((x, y) => x + y, 0) / (xs.length || 1);
    const on = rows.filter((r) => r.retrieve);
    const off = rows.filter((r) => !r.retrieve);
    const summary = {
      retrievalOn: { divergence: +mean(on.map((r) => r.divergence)).toFixed(3), chars: Math.round(mean(on.flatMap((r) => [r.charsA, r.charsB]))) },
      retrievalOff: { divergence: +mean(off.map((r) => r.divergence)).toFixed(3), chars: Math.round(mean(off.flatMap((r) => [r.charsA, r.charsB]))) },
      n: topics.length,
    };
    const out = { summary, rows };
    (window as unknown as Record<string, unknown>).__stagingAB = out;
    console.table([{ arm: "retrieval ON", ...summary.retrievalOn }, { arm: "retrieval OFF", ...summary.retrievalOff }]);
    return out;
  };

  // The FULL staged debate: agenda -> per question, openings then rebuttals.
  //   await runFullDebate({ questions: ["..."] })
  (window as unknown as Record<string, unknown>).runFullDebate = async (opts: {
    a?: string; b?: string; topic?: string; questions?: string[]; model?: string;
    maxExchanges?: number;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, dbt] = await Promise.all([
      import("@/mind/llm/client"),
      import("@/mind/lattice/embedClient"),
      import("@/mind/lattice/packStore"),
      import("@/mind/lattice/packs"),
      import("@/mind/debate"),
    ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes-trismegistus");
    if (!A || !B) throw new Error("unknown pack key");
    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });
    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu");
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b");

    const contexts: { speaker: string; msgs: { kind: string; role: string; text: string }[] }[] = [];
    const rounds = await dbt.runStagedDebate(
      llm, embedder,
      { key: A.key, persona: packA.manifest.persona, pack: packA },
      { key: B.key, persona: packB.manifest.persona, pack: packB },
      opts.topic ?? "whether the phase of the moon should govern when you sow seed",
      opts.questions ?? ["How warm must soil be before sowing?"],
      {
        maxExchanges: opts.maxExchanges ?? 4,
        // The exact labelled context each speaker was handed. Kept because the
        // rebuttal answers stopped matching their own notes, and the only way
        // to find out what they WERE following is to read what they were given.
        onContext: (self, msgs) =>
          contexts.push({
            speaker: self.persona.replace("The Mind's ", ""),
            msgs: msgs.map((m) => ({ kind: m.kind, role: m.role, text: m.content })),
          }),
        onStatement: (s) => console.info(`[open] ${s.persona}: ${s.answer.slice(0, 90)}`),
        onRebuttal: (r) =>
          console.info(
            `[rebut] ${r.persona}${r.yielded ? " YIELDS" : ""} echo=${r.echo.score.toFixed(3)}` +
              ` run=${r.echo.longestRun} rows=${r.rowsTouched.length}: ${r.text.slice(0, 70)}`,
          ),
      },
    );
    llm.dispose();
    embedder.dispose();

    const { judgeEcho, sharedRunText } = await import("@/mind/pure/echoScore");
    const { openerShape, contributionOpenerRate } = await import("@/mind/pure/openerShape");

    // THE CONVERGENCE FLOOR, and it is the control the whole diagnosis turns on.
    //
    // In the opening round neither speaker can see the other, so whatever
    // phrasing they still share is what these weights produce twice over at
    // temperature 0 with no channel between them. Every rebuttal score has to be
    // read against that floor. A rebuttal that echoes no harder than two
    // independent openings do is not evidence of copying at all, and "fixing" it
    // by changing what the rebuttal sees would be treating the wrong cause.
    const floor = rounds.map((q) => {
      const [x, y] = q.openings;
      if (!x || !y) return null;
      const v = judgeEcho(y.answer, x.answer);
      return {
        question: q.question.slice(0, 40),
        echo: +v.score.toFixed(3),
        run: v.longestRun,
        clause: sharedRunText(y.answer, x.answer),
      };
    }).filter(Boolean);

    // The echo table. Scored every turn against what it was answering, so a
    // regression shows up as a number rather than on a careful read.
    //
    // `clause` is the shared run itself. A row that says `run=15` cannot be
    // judged: a copied opener and a technical term two experts on one subject
    // were always going to share look identical as a length.
    const table = rounds.flatMap((q) =>
      q.rebuttals.map((r) => {
        // Every earlier OPPOSING turn, openings included. The openings are part
        // of the transcript the model can read, so they are part of what it can
        // copy, and the first measurement of this missed them.
        const prior = [
          ...q.openings.map((s) => ({ key: s.speakerKey, text: s.answer })),
          ...q.rebuttals
            .slice(0, q.rebuttals.indexOf(r))
            .map((x) => ({ key: x.speakerKey, text: x.text })),
        ]
          .filter((s) => s.key !== r.speakerKey)
          .map((s) => s.text);
        const worst = prior.reduce(
          (acc, t) => {
            const s = sharedRunText(r.text, t);
            const n = s ? s.split(" ").length : 0;
            return n > acc.n ? { n, s } : acc;
          },
          { n: 0, s: "" },
        );
        // The SECOND failure, which the echo score is blind to: opening on the
        // opponent's contribution and finding it wanting, rather than on the
        // claim. Measured 4 of 4 on a run that scored 0.000 echo throughout.
        const shape = openerShape(r.text);
        return {
          speaker: r.persona.replace("The Mind's ", ""),
          echo: +r.echo.score.toFixed(3),
          run: r.echo.longestRun,
          // Shared ORDER. A paraphrase breaks every shingle and keeps this.
          para: +r.echo.paraphrase.toFixed(3),
          flagged: r.echo.echoed,
          clause: worst.s,
          frame: shape.onContribution ? `${shape.noun}/${shape.deficiency}` : "",
          rows: r.rowsTouched.length,
          yielded: r.yielded,
          quoted: /^["“]/.test(r.text.trim()),
          opener: r.text.trim().split(/[.,;:]/)[0].slice(0, 60),
        };
      }),
    );
    const summary = {
      turns: table.length,
      echoed: table.filter((t) => t.flagged).length,
      meanEcho: +(table.reduce((n, t) => n + t.echo, 0) / Math.max(1, table.length)).toFixed(3),
      maxEcho: Math.max(0, ...table.map((t) => t.echo)),
      // The control, alongside the thing it controls for.
      floorEcho: Math.max(0, ...floor.map((f) => (f as { echo: number }).echo)),
      floorRun: Math.max(0, ...floor.map((f) => (f as { run: number }).run)),
      // The number a fix has to move. Echo can be 0.000 while this is 1.00.
      openerFrameRate: +contributionOpenerRate(
        rounds.flatMap((q) => q.rebuttals.map((r) => r.text)),
      ).toFixed(2),
      openingsFrameRate: +contributionOpenerRate(
        rounds.flatMap((q) => q.openings.map((s) => s.answer)),
      ).toFixed(2),
      stillQuoted: table.filter((t) => t.quoted).length,
      zeroRetrievalTurns: table.filter((t) => t.rows === 0).length,
      yields: table.filter((t) => t.yielded).length,
    };
    console.table(floor);
    console.table(table);
    console.info("[echo]", JSON.stringify(summary));
    const out = { rounds, table, floor, contexts, summary };
    (window as unknown as Record<string, unknown>).__fullDebate = out;
    return out;
  };

  // LATTICE-GROUNDED IMAGES (Free's idea): prefetch, then draw.
  //
  // The comparison IS the demonstration, so it draws the same subject three
  // times: once from the bare prompt, once grounded by each lattice. Same
  // weights, same seed conditions, different memory. If the pictures differ,
  // the memory is doing the work.
  //
  // A lattice with nothing visual to say must contribute NOTHING and produce
  // the bare picture, which is why the webdev pack is worth including as the
  // control: `unchanged: true` is a result, not a failure.
  //   await runGroundedImageDemo({ subject: "a tray of seedlings" })
  (window as unknown as Record<string, unknown>).runGroundedImageDemo = async (opts: {
    subject?: string; query?: string; packs?: string[]; k?: number; model?: string;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, ap, vp, imageOut] =
      await Promise.all([
        import("@/mind/llm/client"),
        import("@/mind/lattice/embedClient"),
        import("@/mind/lattice/packStore"),
        import("@/mind/lattice/packs"),
        import("@/mind/lattice/aperture"),
        import("@/mind/pure/visualPrompt"),
        import("@/mind/llm/imageOut"),
      ]);

    const subject = opts.subject ?? "a tray of young seedlings in soil";
    // What to SEARCH for, which need not be what to draw: "damping-off" finds
    // the disease facts that describe how a sick seedling looks.
    const query = opts.query ?? subject;
    const packKeys = opts.packs ?? ["gardening", "hermes-trismegistus", "webdev"];

    // Build every prompt FIRST, so the retrieval work is done before 2.4 GB of
    // image model is resident and competing for memory.
    const embedder = new EmbedClient();
    let embedderLoaded = false;
    const plans: { pack: string; persona: string; prompt: string; added: string[]; unchanged: boolean; facts: string[] }[] = [];
    for (const key of packKeys) {
      const meta = packByKey(key);
      if (!meta) { console.warn(`[grounded] unknown pack ${key}`); continue; }
      const pack = await loadPack(meta.url, { passphrase: meta.passphrase });
      if (!embedderLoaded) { await embedder.load(pack.manifest.embedder, "webgpu"); embedderLoaded = true; }
      const res = await ap.openAperture(pack, embedder, query, opts.k ?? 8);
      const facts = res.results.map((r: { fact: string; quote?: string }) =>
        [r.fact, r.quote].filter(Boolean).join(" "));
      const built = vp.buildImagePrompt(subject, facts);
      plans.push({ pack: key, persona: pack.manifest.persona, ...built, facts });
      console.info(
        `[grounded] ${key}: ${built.unchanged ? "NOTHING VISUAL, prompt unchanged" : built.added.join(" | ")}`,
      );
    }
    embedder.dispose();

    const llm = new MindClient();
    await llm.load(opts.model ?? "janus-pro-1b", {
      onMessage: (m) => console.info(`[grounded] ${m}`),
    });

    const draw = async (label: string, prompt: string) => {
      const { images, ms } = await llm.generateImage(prompt, { count: 1 });
      console.info(`[grounded] drew ${label} in ${(ms / 1000).toFixed(1)}s`);
      return { label, prompt, url: imageOut.toDataURL(images[0]), sec: +(ms / 1000).toFixed(1) };
    };

    const drawn = [await draw("BARE", subject)];
    for (const p of plans) {
      // No point drawing a prompt identical to the bare one; say so instead.
      if (p.unchanged) { console.info(`[grounded] skipping ${p.pack}: identical to BARE`); continue; }
      drawn.push(await draw(p.pack, p.prompt));
    }
    llm.dispose();

    // Show them side by side with their prompts. The whole claim is visual.
    document.getElementById("grounded-out")?.remove();
    const host = document.createElement("div");
    host.id = "grounded-out";
    host.style.cssText =
      "position:fixed;inset:auto 12px 12px auto;z-index:9999;display:flex;gap:10px;" +
      "background:#111;color:#ddd;padding:10px;border:1px solid #444;max-width:92vw;" +
      "font:11px/1.35 monospace;overflow:auto";
    for (const d of drawn) {
      const cell = document.createElement("div");
      cell.style.cssText = "width:220px";
      const img = new Image();
      img.src = d.url;
      img.style.cssText = "width:220px;height:220px;display:block";
      const cap = document.createElement("div");
      cap.textContent = `${d.label}: ${d.prompt}`;
      cap.style.cssText = "margin-top:6px;word-break:break-word";
      cell.append(img, cap);
      host.appendChild(cell);
    }
    document.body.appendChild(host);

    const out = { subject, query, plans, drawn };
    (window as unknown as Record<string, unknown>).__grounded = out;
    return out;
  };

  // JANUS: one model, two heads. Chat AND images from the same weights.
  //
  // Deliberately two separate calls rather than one mode flag, per Free: keep
  // the image and chat systems apart for now and wire them together later only
  // if that turns out to be worth it.
  //
  // The interesting risk is NOT the pictures. It is whether a 1B unified
  // multimodal model reads a lattice as well as Gemma 3 1B does, so this
  // reports a text sample alongside the image.
  //   await runImageDemo({ prompt: "a mountain lake at dawn" })
  (window as unknown as Record<string, unknown>).runImageDemo = async (opts: {
    prompt?: string; count?: number; temperature?: number; askText?: string;
  } = {}) => {
    const [{ MindClient }, imageOut] = await Promise.all([
      import("@/mind/llm/client"),
      import("@/mind/llm/imageOut"),
    ]);
    const llm = new MindClient();
    const loadMs = await llm.load("janus-pro-1b", {
      onMessage: (m) => console.info(`[janus] ${m}`),
    });
    console.info(`[janus] loaded in ${(loadMs / 1000).toFixed(1)}s`);

    // Text first, so a failure there is not mistaken for an image failure.
    const askText = opts.askText ?? "In one sentence, what is a WebGPU bind group for?";
    let text = "";
    try {
      const t = await llm.generate([{ role: "user", content: askText }], { maxNewTokens: 96 });
      text = t.text.trim();
      console.info(`[janus TEXT] ${text}`);
    } catch (e) {
      text = `TEXT FAILED: ${String(e)}`;
      console.error(text);
    }

    const prompt = opts.prompt ?? "a quiet mountain lake at dawn, mist on the water";
    const t0 = performance.now();
    const { images, ms } = await llm.generateImage(prompt, {
      count: opts.count ?? 1,
      temperature: opts.temperature ?? 1,
    });
    llm.dispose();

    // Show them, rather than reporting that pixels exist. A tensor of the right
    // shape full of noise passes every check a number can make.
    const urls = images.map((im) => imageOut.toDataURL(im));
    const host = document.createElement("div");
    host.id = "janus-out";
    host.style.cssText =
      "position:fixed;right:12px;bottom:12px;z-index:9999;display:flex;gap:8px;flex-wrap:wrap;" +
      "background:#111;padding:8px;border:1px solid #444;max-width:60vw";
    for (const u of urls) {
      const img = new Image();
      img.src = u;
      img.style.cssText = "width:192px;height:192px;image-rendering:pixelated";
      host.appendChild(img);
    }
    document.getElementById("janus-out")?.remove();
    document.body.appendChild(host);

    const out = {
      loadSec: +(loadMs / 1000).toFixed(1),
      text,
      prompt,
      count: images.length,
      size: images[0] ? `${images[0].width}x${images[0].height}x${images[0].channels}` : "none",
      imageSec: +(ms / 1000).toFixed(1),
      wallSec: +((performance.now() - t0) / 1000).toFixed(1),
      urls,
    };
    (window as unknown as Record<string, unknown>).__janus = out;
    console.info("[janus]", JSON.stringify({ ...out, urls: `${urls.length} data urls` }));
    return out;
  };

  // CONTEXT COMPRESSION, forced. A real debate at a deliberately tiny window,
  // so the fold path actually fires and can be checked.
  //
  // The assertion that matters is not "it stayed under budget", which is
  // arithmetic. It is that NOTHING SPOKEN WAS PARAPHRASED: every `spoken`
  // message in every context the model actually saw must still contain, word for
  // word, something a speaker really said. A transcript that quietly rewords an
  // answer would let the next rebuttal argue against words nobody used, and it
  // would read as perfectly good prose while doing it.
  //   await runCompressionDemo({ limit: 900 })
  (window as unknown as Record<string, unknown>).runCompressionDemo = async (opts: {
    a?: string; b?: string; topic?: string; question?: string; model?: string;
    limit?: number; maxExchanges?: number;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, dbt, fit] =
      await Promise.all([
        import("@/mind/llm/client"),
        import("@/mind/lattice/embedClient"),
        import("@/mind/lattice/packStore"),
        import("@/mind/lattice/packs"),
        import("@/mind/debate"),
        import("@/mind/contextFit"),
      ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes-trismegistus");
    if (!A || !B) throw new Error("unknown pack key");
    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });
    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu");
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b");

    const fits: { persona: string; line: string; gist?: string }[] = [];
    const contexts: { persona: string; msgs: { kind: string; content: string }[] }[] = [];
    // 0 means "use the model's real budget", which is the control arm: it should
    // fire nothing at all, and a run where it does is telling you something.
    const limit = opts.limit === undefined ? 900 : opts.limit;

    const rounds = await dbt.runStagedDebate(
      llm, embedder,
      { key: A.key, persona: packA.manifest.persona, pack: packA },
      { key: B.key, persona: packB.manifest.persona, pack: packB },
      opts.topic ?? "whether the phase of the moon should govern when you sow seed",
      [opts.question ?? "How warm must soil be before sowing?"],
      {
        maxExchanges: opts.maxExchanges ?? 6,
        contextLimit: limit || undefined,
        onStatement: (s) => console.info(`[open] ${s.persona}: ${s.answer.slice(0, 80)}`),
        onRebuttal: (r) => console.info(`[rebut] ${r.persona}${r.yielded ? " YIELDS" : ""}: ${r.text.slice(0, 80)}`),
        onFit: (sp, r) => {
          const line = fit.describeFit(r);
          fits.push({ persona: sp.persona, line, gist: r.gist });
          console.info(`[budget] ${sp.persona}: ${line}`);
          if (r.gist) console.info(`         gist: ${r.gist}`);
        },
        onContext: (sp, msgs) => contexts.push({
          persona: sp.persona,
          msgs: msgs.map((m) => ({ kind: m.kind, content: m.content })),
        }),
      },
    );

    // Everything anybody actually said, verbatim.
    const utterances = rounds.flatMap((q) => [
      ...q.openings.map((s) => s.answer),
      ...q.rebuttals.map((r) => r.text),
    ]);
    const violations: { persona: string; content: string }[] = [];
    for (const c of contexts) {
      for (const m of c.msgs) {
        if (m.kind !== "spoken") continue;
        // The question frame is not an utterance; everything else must be one.
        if (m.content.startsWith("The question is:")) continue;
        if (!utterances.some((u) => u.length > 0 && m.content.includes(u))) {
          violations.push({ persona: c.persona, content: m.content.slice(0, 120) });
        }
      }
    }

    llm.dispose();
    embedder.dispose();
    const out = {
      limit: limit || "model default",
      folds: fits.length,
      gists: fits.filter((f) => f.gist).map((f) => `${f.persona}: ${f.gist}`),
      verbatimViolations: violations,
      contextsSeen: contexts.length,
      peakTokens: Math.max(0, ...rounds.flatMap((q) => q.rebuttals.map((r) => r.fit?.before ?? 0))),
      rounds,
    };
    (window as unknown as Record<string, unknown>).__compression = out;
    console.info(
      violations.length === 0
        ? `VERBATIM OK across ${contexts.length} contexts, ${fits.length} folds`
        : `VERBATIM VIOLATED ${violations.length}x`,
    );
    return out;
  };

  // The staged protocol on one question: prepare, read, prepare, answer, with
  // both agents answering INDEPENDENTLY. The comparison against the earlier
  // paraphrasing transcripts is the whole point.
  //   await runStagedDemo({ topic: "...", question: "..." })
  (window as unknown as Record<string, unknown>).runStagedDemo = async (opts: {
    a?: string; b?: string; topic?: string; question?: string; model?: string;
  } = {}) => {
    const [{ MindClient }, { EmbedClient }, { loadPack }, { packByKey }, dbt] = await Promise.all([
      import("@/mind/llm/client"),
      import("@/mind/lattice/embedClient"),
      import("@/mind/lattice/packStore"),
      import("@/mind/lattice/packs"),
      import("@/mind/debate"),
    ]);
    const A = packByKey(opts.a ?? "gardening");
    const B = packByKey(opts.b ?? "hermes-trismegistus");
    if (!A || !B) throw new Error("unknown pack key");
    const packA = await loadPack(A.url, { passphrase: A.passphrase });
    const packB = await loadPack(B.url, { passphrase: B.passphrase });
    const embedder = new EmbedClient();
    await embedder.load(packA.manifest.embedder, "webgpu");
    const llm = new MindClient();
    await llm.load(opts.model ?? "gemma-3-1b");

    const topic = opts.topic ?? "whether the phase of the moon should govern when you sow seed";
    const question = opts.question ?? "How warm must soil be before sowing?";
    const sa = { key: A.key, persona: packA.manifest.persona, pack: packA };
    const sb = { key: B.key, persona: packB.manifest.persona, pack: packB };

    const stagedA = await dbt.prepareSpeaker(llm, embedder, sa, sb, topic, true);
    const stagedB = await dbt.prepareSpeaker(llm, embedder, sb, sa, topic, false);
    const statements = await dbt.runOpeningRound(
      llm, embedder, sa, sb, stagedA, stagedB, question, 0, undefined,
      (s) => console.info(`[staged] ${s.persona}: ${s.answer.slice(0, 100)}`),
    );

    llm.dispose();
    embedder.dispose();
    const out = { topic, question, stagedA, stagedB, statements };
    (window as unknown as Record<string, unknown>).__staged = out;
    return out;
  };
}

/**
 * `?lab` opens the pipeline workbench instead of the visitor page.
 *
 * A query flag rather than a router: this bay has exactly two surfaces and
 * pulling in routing to express that would be more machinery than the choice
 * deserves. It also keeps the lab out of the way of anyone who lands on
 * mind.free.vet without asking for it.
 *
 * NOT dev-gated. Free tunes against the real deployed build, and a workbench
 * that only exists on localhost cannot be used to diagnose the thing visitors
 * actually meet.
 */
const wantsLab = new URLSearchParams(location.search).has("lab");

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {wantsLab ? (
      <DebateLab
        onExit={() => {
          location.search = "";
        }}
      />
    ) : (
      <MindPage />
    )}
  </StrictMode>,
);
